import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { useNavigation } from '../../hooks/useNavigation';
import { getVmSummary } from '../../services/dashboardService';

function Icon({ name }) {
  return <span className="material-symbols-rounded">{name}</span>;
}

/**
 * One metric tile. `metric.average` is null whenever no machine reported the
 * value, and that stays visible as "no data" — never as 0%.
 */
function MetricTile({ icon, label, metric, unavailableLabel, formatPercent }) {
  const average = typeof metric?.average === 'number' ? metric.average : null;

  return (
    <div className="dashboard-vm-metric">
      <Icon name={icon} />
      <span>{label}</span>
      {average === null ? (
        <strong className="is-unavailable">{unavailableLabel}</strong>
      ) : (
        <strong>{formatPercent(average)}</strong>
      )}
      <div className={`fleet-bar${average === null ? ' is-unavailable' : ''}`}>
        {average !== null && (
          <span style={{ width: `${Math.max(0, Math.min(100, average))}%` }} />
        )}
      </div>
    </div>
  );
}

/**
 * Dashboard panel with the current state of the tenant's VM fleet.
 *
 * Reads the shared /dashboard/vm-summary aggregate once — never one request
 * per machine — and reuses the vmMonitoring labels from the full page it links
 * to, so the two surfaces never drift apart.
 */
function DashboardVmCard() {
  const { t } = useTranslation();
  const { getTenantPath } = useNavigation();
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState('loading');

  const loadSummary = useCallback(async () => {
    setState('loading');
    try {
      setSummary(await getVmSummary());
      setState('ready');
    } catch (err) {
      console.error('Dashboard VM summary load failed:', err);
      setSummary(null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const machines = summary?.machines || {};
  const metrics = summary?.metrics || {};
  const machineCount = machines.total || 0;
  const unavailableLabel = t('vmMonitoring.metricUnavailable');
  const formatPercent = (value) => t('vmMonitoring.percent', { value });

  return (
    <div className="panel panel-wide dashboard-vm-card" data-testid="dashboard-vm-card">
      <div className="panel-header dashboard-vm-header">
        <div className="panel-title">
          <h3>{t('dashboard.virtualMachines')}</h3>
          {state === 'ready' && (
            <span className="badge">
              {t('vmMonitoring.machineCount', { count: machineCount })}
            </span>
          )}
        </div>
        <Link to={getTenantPath('vm-monitoring')}>{t('dashboard.viewMore')}</Link>
      </div>

      {state === 'loading' && (
        <div className="dashboard-vm-loading" aria-busy="true">
          <CircularProgress size={28} />
        </div>
      )}

      {state === 'error' && (
        <div className="dashboard-vm-error" role="alert">
          <p>{t('vmMonitoring.summaryError')}</p>
          <button type="button" className="btn outline" onClick={loadSummary}>
            {t('vmMonitoring.retry')}
          </button>
        </div>
      )}

      {state === 'ready' && machineCount === 0 && (
        <div className="panel-empty">{t('dashboard.noVirtualMachines')}</div>
      )}

      {state === 'ready' && machineCount > 0 && (
        <>
          <div className="dashboard-vm-grid">
            <MetricTile
              icon="desktop_windows"
              label={t('vmMonitoring.avgCpu')}
              metric={metrics.cpu_percent}
              unavailableLabel={unavailableLabel}
              formatPercent={formatPercent}
            />
            <MetricTile
              icon="memory"
              label={t('vmMonitoring.avgMemory')}
              metric={metrics.memory_percent}
              unavailableLabel={unavailableLabel}
              formatPercent={formatPercent}
            />
            <MetricTile
              icon="storage"
              label={t('vmMonitoring.avgDisk')}
              metric={metrics.disk_percent}
              unavailableLabel={unavailableLabel}
              formatPercent={formatPercent}
            />
            <div className="dashboard-vm-metric">
              <Icon name="lan" />
              <span>{t('vmMonitoring.fleetStatus')}</span>
              <strong className="is-compact">
                {t('vmMonitoring.fleetStatusValue', {
                  online: machines.online || 0,
                  offline: machines.offline || 0,
                })}
              </strong>
            </div>
          </div>

          <p className="dashboard-vm-footnote">
            {t('vmMonitoring.telemetryCoverage', {
              reporting: summary?.reporting_machines || 0,
              total: machineCount,
            })}
          </p>
        </>
      )}
    </div>
  );
}

export default DashboardVmCard;
